import type { FetchSoapNotesDto, SoapImage, SoapNoteApiItem } from './procedure-api.type'

// BE list responses carry the preview in `thumbnail`; older responses used `image`.
type RawSoapImage = Omit<SoapImage, 'image'> & { image?: string }

export const normalizeSoapImage = (image: RawSoapImage): SoapImage => ({
  ...image,
  image: image.image || image.thumbnail || '',
})

export const normalizeSoapNotes = (notes: SoapNoteApiItem[]): SoapNoteApiItem[] =>
  notes.map((note) => ({
    ...note,
    soap_images: (note.soap_images || []).map(normalizeSoapImage),
  }))

const BASE64_CHUNK_SIZE = 8192

export const soapImageBlobToDataUrl = async (blob: Blob): Promise<string> => {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  const chunks: string[] = []
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_SIZE) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK_SIZE)))
  }
  return `data:${blob.type};base64,${btoa(chunks.join(''))}`
}

// In-flight uploads by temp image id — decides which optimistic images survive refetches.
const inFlightUploads = new Map<number, string>()

export const getSoapNotesScopeKey = ({ patientPid, encounterUid }: FetchSoapNotesDto): string =>
  `${patientPid}:${encounterUid}`

export const registerPendingSoapImageUpload = (tempImageId: number, scopeKey: string): void => {
  inFlightUploads.set(tempImageId, scopeKey)
}

export const unregisterPendingSoapImageUpload = (tempImageId: number): void => {
  inFlightUploads.delete(tempImageId)
}

export const isSoapImageUploadInFlight = (tempImageId: number): boolean =>
  inFlightUploads.has(tempImageId)

export const getPendingSoapImageUploadCount = (scopeKey: string): number => {
  let count = 0
  inFlightUploads.forEach((uploadScopeKey) => {
    if (uploadScopeKey === scopeKey) {
      count += 1
    }
  })
  return count
}

export const resetSoapImageUploadRegistry = (): void => {
  inFlightUploads.clear()
}

export const isPendingSoapImage = (image: SoapImage): boolean => image.id < 0

export const hasPendingSoapImages = (notes: SoapNoteApiItem[] | undefined): boolean =>
  Boolean(notes?.some((note) => note.soap_images?.some(isPendingSoapImage)))

// Keeps only optimistic images whose upload is still in flight.
export const filterStillUploadingSoapImages = (images: SoapImage[]): SoapImage[] =>
  images.filter((image) => isPendingSoapImage(image) && isSoapImageUploadInFlight(image.id))

const collectStillUploadingSoapImages = (notes: SoapNoteApiItem[]): SoapImage[] =>
  filterStillUploadingSoapImages(notes.flatMap((note) => note.soap_images || [])).map((image) => ({
    id: image.id,
    image: image.image,
    procedure_id: image.procedure_id,
  }))

// fetchSoapNotes merge(): server data wins, but images still uploading are re-attached.
export const mergeSoapNotesPreservingPendingImages = (
  currentCacheData: SoapNoteApiItem[],
  responseData: SoapNoteApiItem[],
): SoapNoteApiItem[] => {
  const stillUploading = collectStillUploadingSoapImages(currentCacheData)

  if (stillUploading.length === 0) {
    return responseData
  }

  if (responseData.length === 0) {
    return [{ ...currentCacheData[0], soap_images: stillUploading }]
  }

  const [firstNote, ...restNotes] = responseData

  return [
    { ...firstNote, soap_images: [...(firstNote.soap_images || []), ...stillUploading] },
    ...restNotes,
  ]
}
